import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { GlobalContext } from '@/src/contexts/GlobalContext';
import { useAdobeLaunch } from '@/src/components/Common/utils/adobeLaunch';

import styles from './index.module.scss';
import { LogoutReminderPopupType } from '../../constants';
import { useWindowSize } from '../../CommonHooks';

const getRemainingTime = (sessionIdleTime, sessionServerTimeStamp) => {
    const now = window.ssTime || new Date().getTime();
    if (now >= sessionIdleTime) {
        return 0;
    }
    return new Date(sessionIdleTime - sessionServerTimeStamp).getMinutes() + 1;
};

export default function LogoutReminderPopup({
    logoutReminderPopupState,
    sessionIdleTimeRef,
    resetLoginTimeout,
    calcTimeOutLogout,
    highSecurityLogout,
    onClose
}) {
    const { t } = useTranslation();
    const { sessionServerTimeStamp } = useContext(GlobalContext);
    const { isMobileSize } = useWindowSize();
    const { sendAdobeEvent } = useAdobeLaunch();

    useEffect(() => {
        if (logoutReminderPopupState === LogoutReminderPopupType.warning) {
            if (window.globalConfig.ENABLE_WA_TAGGING_FOR_LOGOUT_MESSAGE_AND_RACING_CLEAR_ALL) {
                sendAdobeEvent({ event: 'interaction', interaction: { eventID: 'eWin_logout2min' } });
            }
        }

        if (logoutReminderPopupState === LogoutReminderPopupType.timeout) {
            highSecurityLogout();
            if (window.globalConfig.ENABLE_WA_TAGGING_FOR_LOGOUT_MESSAGE_AND_RACING_CLEAR_ALL) {
                sendAdobeEvent({ event: 'interaction', interaction: { eventID: 'eWin_autologout' } });
            }
        }
    }, [logoutReminderPopupState]);

    if (logoutReminderPopupState === LogoutReminderPopupType.closed) return null;

    const currentReminderText =
        logoutReminderPopupState === LogoutReminderPopupType.warning
            ? t('LB_COMING_SOON_LOGOUT_REMIND', {
                  minutes: getRemainingTime(sessionIdleTimeRef.current, sessionServerTimeStamp)
              })
            : t('LB_AUTO_LOGOUT_REMIND');

    const handleClickContinue = (e) => {
        onClose(e);
        resetLoginTimeout(false);
        if (window.globalConfig.ENABLE_WA_TAGGING_FOR_LOGOUT_MESSAGE_AND_RACING_CLEAR_ALL) {
            sendAdobeEvent({ event: 'interaction', interaction: { eventID: 'eWin_logout2min_ClickKeepLogin' } });
        }
    };

    const handleClickLogout = (e) => {
        onClose(e);
        calcTimeOutLogout();
        if (window.globalConfig.ENABLE_WA_TAGGING_FOR_LOGOUT_MESSAGE_AND_RACING_CLEAR_ALL) {
            sendAdobeEvent({ event: 'interaction', interaction: { eventID: 'eWin_logout2min_ClickLogout' } });
        }
    };

    const handleClickLogin = (e) => {
        onClose(e);
        if (isMobileSize) {
            document.getElementById('betslip-container').style.display = 'inherit';
            document.getElementById('divBetSlipNormal').style.left = 'calc(100vw - 90vw)';
            document.getElementById('betslip-over-lay').style.left = '0';
        }
        setTimeout(() => {
            document.getElementById('login-password-input').focus();
        }, 200);
        if (window.globalConfig.ENABLE_WA_TAGGING_FOR_LOGOUT_MESSAGE_AND_RACING_CLEAR_ALL && logoutReminderPopupState === LogoutReminderPopupType.timeout) {
            sendAdobeEvent({ event: 'interaction', interaction: { eventID: 'eWin_autologout_ClickLogin' } });
        }
    };

    const handleClickClose = (e) => {
        onClose(e);
        if (window.globalConfig.ENABLE_WA_TAGGING_FOR_LOGOUT_MESSAGE_AND_RACING_CLEAR_ALL && logoutReminderPopupState === LogoutReminderPopupType.timeout) {
            sendAdobeEvent({ event: 'interaction', interaction: { eventID: 'eWin_autologout_ClickCancel' } });
        }
    };

    return createPortal(
        <div className={styles.overlay}>
            <div className={styles.popup}>
                <div className={styles.tips}>{currentReminderText}</div>
                <div className={styles.buttonWrap}>
                    {logoutReminderPopupState === LogoutReminderPopupType.warning ? (
                        <>
                            <div className={clsx(styles.normalButton, styles.button)} onClick={handleClickLogout}>
                                {t('LB_REMIND_LOGOUT')}
                            </div>
                            <div className={clsx(styles.blueButton, styles.button)} onClick={handleClickContinue}>
                                {t('LB_REMIND_CONTINUE')}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={clsx(styles.normalButton, styles.button)} onClick={handleClickClose}>
                                {t('LB_REMIND_CANCEL')}
                            </div>
                            <div className={clsx(styles.blueButton, styles.button)} onClick={handleClickLogin}>
                                {t('LB_REMIND_LOGIN')}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.getElementById('root')
    );
}
